/**
 * Legacy drill-through into Spreading Summary, translated to this report's URL.
 *
 * Legacy Summary is a drill-through *target*: the Cutting Daily Output dashboards (MDP and SDP)
 * open `sftsRpt-spreading-spreadingSummaryReport` with their own parameter names, and
 * `spreadingSummaryReport.js#initDefaultData` (lines 151-212) copies them into the screen's filter
 * bar. Those names are reproduced here so a bookmark or a dashboard link that has been in use for
 * years still lands on the right rows.
 *
 * Read out of the legacy file, not guessed:
 *
 * | Legacy param     | Legacy destination                | Here                     |
 * |------------------|-----------------------------------|--------------------------|
 * | `building`       | `#building`                       | `building`               |
 * | `b_no`           | `#b_no`                           | `b_no`                   |
 * | `cutting_table`  | `#spreading_table`                | `spreading_table`        |
 * | `factory`        | `#factory`                        | `factory`                |
 * | `mo`             | `#mo`                             | `mo`                     |
 * | `startDate`      | `#endoptime_start` + ` 00:00:00`  | `from`                   |
 * | `endDate`        | `#endoptime_end` + ` 23:59:59`    | `to`                     |
 * | `psddMonth` + `period` + `sdpOrMdp` | `#crd_*` or `#tcpsdd_*` | **not translatable** |
 *
 * The last row is a real loss and is stated rather than papered over. `period` splits `psddMonth`
 * into a half-month or whole-month window, and `sdpOrMdp` decides whether that window applies to
 * CRD (`newCustomerRequestDate`) or TCPSDD (`newpsdd`). Neither CRD nor TCPSDD is a filter on this
 * report — the owner cut both on 2026-08-12 — so there is nowhere to put the window, and applying
 * it to the spreading date instead would silently answer a different question. The link still
 * opens, on everything else it carried, and the page says what it could not honour.
 */

export const LEGACY_PARAMS = [
  'building',
  'b_no',
  'cutting_table',
  'mo',
  'startDate',
  'endDate',
  'psddMonth',
  'period',
  'sdpOrMdp',
] as const;

/** Legacy names that map straight onto one of this report's filters. */
const DIRECT: ReadonlyArray<readonly [legacy: string, here: string]> = [
  ['building', 'building'],
  ['b_no', 'b_no'],
  ['cutting_table', 'spreading_table'],
  ['mo', 'mo'],
  ['startDate', 'from'],
  ['endDate', 'to'],
];

export interface DeepLinkTranslation {
  /** The rewritten query string, with the legacy names removed. */
  params: URLSearchParams;
  /** What could not be carried across, in the reader's words. Empty when everything was. */
  unsupported: string | null;
}

/** Whether this URL still carries any legacy dashboard parameter. */
export function hasLegacyParams(params: URLSearchParams): boolean {
  return LEGACY_PARAMS.some((name) => params.has(name));
}

/**
 * Translate the legacy parameters once, on mount. `factory` needs no entry: legacy and this report
 * spell it the same, so it is already in the right place.
 */
export function translateDeepLink(params: URLSearchParams): DeepLinkTranslation {
  const next = new URLSearchParams(params);

  for (const [legacy, here] of DIRECT) {
    const value = params.get(legacy);
    next.delete(legacy);
    if (value !== null && value !== '' && !params.has(here)) {
      next.set(here, value);
    }
  }

  const psddMonth = params.get('psddMonth');
  const sdpOrMdp = params.get('sdpOrMdp');
  for (const name of ['psddMonth', 'period', 'sdpOrMdp']) next.delete(name);

  // The dashboards always land on Summary; `view` is this report's own state, not a legacy one.
  if (!params.has('view')) {
    next.set('view', 'summary');
  }

  const dateField = sdpOrMdp === 'newpsdd' ? 'TCPSDD' : 'CRD';
  const unsupported =
    psddMonth === null || psddMonth === ''
      ? null
      : `This link also narrowed by ${dateField} for ${psddMonth}. `
        + `${dateField} is not a filter on this report, so that part was not applied — `
        + 'the spreading date range below is the only period in force.';

  return { params: next, unsupported };
}
